// components/ProductCard.jsx
import { useEffect, useState } from 'react';
import { formatUGX, buildOrderLink } from '../api/getProducts';
import { useCart } from 'context/CartContext';
import { CartIcon } from 'components/icons/CartIcon';

const BADGE_STYLES = {
  new: 'bg-accent text-black',
  hot: 'bg-[#ff6b35] text-white',
  used: 'bg-white/15 text-text',
  sale: 'bg-warning text-black',
};

const BADGE_LABELS = {
  new: 'New',
  hot: 'Hot',
  used: 'UK Used',
  sale: 'Sale',
};

export function ProductCard({ product }) {
  const { items, addItem } = useCart();
  const inCart = items.some((item) => item.id === product.id);

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-card transition-all hover:-translate-y-1 hover:border-accent/25 hover:shadow-xl hover:shadow-black/40">
      {product.badge && (
        <span
          className={`absolute left-3 top-3 z-10 rounded-full px-2.5 py-1 text-[0.68rem] font-extrabold uppercase tracking-wide ${BADGE_STYLES[product.badge]}`}
        >
          {BADGE_LABELS[product.badge]}
        </span>
      )}

      <div className="h-48 overflow-hidden bg-bg3">
        <img
          src={product.img}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      <div className="p-4">
        <div className="mb-1 text-[0.68rem] font-bold uppercase tracking-wide text-accent">
          {product.cat}
        </div>
        <div className="mb-1 text-sm font-extrabold leading-snug">{product.name}</div>
        <p className="mb-3 text-xs leading-relaxed text-text-muted">
          {product.spec?.slice(0, 50)}...
        </p>

        <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3">
          <div className="text-base font-black text-accent">
            <sup className="text-[0.65rem] font-bold">UGX</sup> {formatUGX(product.price)}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => addItem(product, 1)}
              aria-label={
                inCart ? `${product.name} is in your cart` : `Add ${product.name} to cart`
              }
              title={inCart ? 'In your cart — tap to add another' : 'Add to cart'}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${
                inCart
                  ? 'border-accent bg-accent text-black'
                  : 'border-white/10 text-text-muted hover:border-accent/40 hover:text-accent'
              }`}
            >
              {inCart ? <CheckMark /> : <CartIcon className="h-4 w-4" />}
            </button>

            <a
              href={buildOrderLink(product)}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border border-accent/20 bg-accent-dim px-3.5 py-1.5 text-xs font-extrabold text-accent transition-colors hover:bg-accent hover:text-black"
            >
              Order →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function CheckMark() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      className="h-3.5 w-3.5"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
